import Button from '@/components/UI/Button';
import { errorToast, successToast } from '@/helpers';
import { _isNotBlank, _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ApiService, ResponseType } from '@/support/ApiService';
import { parseApiErrorResponse } from '@/utils/apiErrorResponse';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { ErrorMessage } from '@hookform/error-message';
import type { FC } from 'react';
import { useForm } from 'react-hook-form';
import { USER_GROUP_NAME_MAX_LENGTH, type UserGroupCreatePayload, type UserGroupRow } from '@/model/UserGroups.model';

const NAME_ERROR_ID = 'user-group-name-error';

interface CreateUserGroupFormValues {
  name: string;
}

interface CreateUserGroupModalProps {
  closeModal: () => void;
  onCreated: (group: UserGroupRow) => void;
}

const CreateUserGroupModal: FC<CreateUserGroupModalProps> = ({ closeModal, onCreated }) => {
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserGroupFormValues>({ defaultValues: { name: '' } });

  const onSubmit = async ({ name }: CreateUserGroupFormValues) => {
    const body: UserGroupCreatePayload = { name: name.trim() };
    const response = await ApiService.call({
      endpoint: 'POST /api/v1/user-groups',
      path: undefined,
      body,
      options: { onError: { toast: false, capture: false, throw: false } },
    });

    if (response.type === ResponseType.Error) {
      const fieldMessage = nameValidationMessage(response);
      if (_isNotBlank(fieldMessage)) {
        setError('name', { type: 'server', message: fieldMessage });
      } else {
        errorToast('Could not create the group');
        ExceptionHandler.captureException(
          new InVivoError('Could not create user group', { cause: response.error, slug: 'user-group-create' })
        );
      }
      return;
    }

    const created = response.body;
    successToast(`Created group ${created.name}`);
    try {
      onCreated(created);
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('User group created but the list could not be refreshed', {
          cause: error,
          slug: 'user-group-created-callback',
        })
      );
    }
    closeModal();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="center mw6 ui-card"
      data-test-component="CreateUserGroupModal"
      data-test-element="container"
    >
      <div className="pa4" data-test-component="CreateUserGroupModal" data-test-element="header">
        <h3 className="normal f4" data-testid="title">
          Create group
        </h3>
      </div>
      <div data-test-component="CreateUserGroupModal" data-test-element="body">
        <div className="pb4 ph4">
          <label htmlFor="user-group-name">Group name (required)</label>
          <input
            id="user-group-name"
            type="text"
            data-testid="name-input"
            style={{ marginBottom: 0 }}
            className={_notNil(errors.name) ? 'input__error' : ''}
            aria-invalid={_notNil(errors.name)}
            aria-describedby={_notNil(errors.name) ? NAME_ERROR_ID : undefined}
            {...register('name', {
              required: 'This field is required',
              validate: (value) => {
                if (!_isNotBlank(value)) {
                  return 'This field is required';
                }
                return (
                  value.trim().length <= USER_GROUP_NAME_MAX_LENGTH ||
                  `Maximum of ${USER_GROUP_NAME_MAX_LENGTH} characters`
                );
              },
            })}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => (
              <small id={NAME_ERROR_ID} role="alert" data-testid="name-error-message" className="red db pt2">
                {message}
              </small>
            )}
          />
        </div>
      </div>
      <div
        className="bt b--moon-gray bg-white pa3"
        data-test-component="CreateUserGroupModal"
        data-test-element="footer"
      >
        <Button submit className="mr2" disabled={isSubmitting} testId="submit-button">
          {isSubmitting ? 'Creating…' : 'Create group'}
        </Button>
        <Button plain onClick={closeModal} disabled={isSubmitting} testId="cancel-button">
          Cancel
        </Button>
      </div>
    </form>
  );
};

const nameValidationMessage = (error: unknown): string | undefined => {
  const { details, status } = parseApiErrorResponse(error);
  if (status !== 400 && status !== 422) {
    return undefined;
  }
  return details.find(({ field }) => field === 'name')?.messages[0];
};

export default CreateUserGroupModal;
